// ShareBite NGO Rating average computation (Task 6.3).
//
// Pure aggregation used by the NGO Directory to display an NGO's average
// Rating. The function is platform-independent and side-effect free; callers
// (the app's directory view and any server-side aggregation) apply the same
// rule so client and server never disagree on a displayed average.
//
// Design reference: design.md "Property 14: Rating average computation".
// Requirements:
//   16.6 - The NGO Directory displays each NGO's average Rating computed from
//          all submitted star scores and rounded to one decimal place.
//   16.7 - While an NGO has zero submitted Ratings, the Directory shows a
//          no-ratings indication instead of an average Rating value.
/**
 * Round a number to one decimal place using a single canonical scheme.
 *
 * Uses round-half-up on the scaled value (`Math.round(x * 10) / 10`). The test
 * oracle computes the expected value with this exact same expression so the
 * two never diverge due to floating-point representation.
 */
function roundToOneDecimal(value) {
    return Math.round(value * 10) / 10;
}
/**
 * Compute the average of a multiset of submitted star scores.
 *
 * For a non-empty set of scores, returns `{ hasRatings: true, average }` where
 * `average` is the arithmetic mean rounded to one decimal place (Req 16.6).
 * For an empty set, returns `{ hasRatings: false }` so the caller renders a
 * no-ratings indication rather than a numeric average (Req 16.7).
 *
 * This function is pure: it reads only its argument and returns a result
 * without mutating any input or producing side effects.
 *
 * @param starScores The submitted star scores for an NGO (each typically 1..5).
 * @returns A discriminated {@link RatingAverage} result.
 */
export function computeRatingAverage(starScores) {
    if (starScores.length === 0) {
        return { hasRatings: false };
    }
    const sum = starScores.reduce((acc, score) => acc + score, 0);
    const mean = sum / starScores.length;
    return { hasRatings: true, average: roundToOneDecimal(mean) };
}
//# sourceMappingURL=ratingAverage.js.map